import React, { useMemo } from 'react';
import {
  Cake, PartyPopper, CalendarDays, UserPlus2, Users2, Building2,
  Activity, ChevronRight, CalendarClock,
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { EmptyState } from '../common/States';
import { cn } from '../../lib/utils';
import { CHART_COLORS, chartColor, tooltipContentStyle, tooltipCursor, axisProps } from '../../lib/chartTheme';

// ---------- helpers ----------
const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fullName = (e) =>
  [e?.first_name, e?.last_name].filter(Boolean).join(' ').trim() ||
  e?.name || e?.employee_id || 'Employee';

const initials = (e) => {
  const n = fullName(e);
  const parts = n.split(' ').filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'E';
};

// Days until next anniversary of a month/day (ignores year).
const daysUntilAnnual = (date, today) => {
  if (!date) return null;
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let next = new Date(today.getFullYear(), date.getMonth(), date.getDate());
  if (next < t) next = new Date(today.getFullYear() + 1, date.getMonth(), date.getDate());
  return Math.round((next - t) / 86400000);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonthDay = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
const relativeDay = (n) => (n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : `in ${n} days`);

function Avatar({ emp, size = 'md' }) {
  const url = emp?.profile_photo_url;
  const dim = size === 'sm' ? 'h-8 w-8 text-[11px]' : 'h-9 w-9 text-xs';
  if (url) {
    return <img src={url} alt={fullName(emp)} className={cn('rounded-full object-cover ring-2 ring-card', dim)} />;
  }
  return (
    <div className={cn('grid place-items-center rounded-full bg-primary/10 font-semibold text-primary ring-2 ring-card', dim)}>
      {initials(emp)}
    </div>
  );
}

// A consistent premium section card with header + optional action.
export function SectionCard({ title, icon: Icon, action, children, className, bodyClassName }) {
  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 border-b border-border/60 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          {Icon && <Icon className="h-4 w-4 text-primary" />}
          {title}
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent className={cn('flex-1 p-4', bodyClassName)}>{children}</CardContent>
    </Card>
  );
}

// ---------- Birthdays ----------
export function UpcomingBirthdays({ employees = [], days = 30 }) {
  const today = useMemo(() => new Date(), []);
  const list = useMemo(() => {
    return employees
      .map((e) => ({ e, d: parseDate(e.date_of_birth) }))
      .filter((x) => x.d)
      .map((x) => ({ ...x, in: daysUntilAnnual(x.d, today) }))
      .filter((x) => x.in !== null && x.in <= days)
      .sort((a, b) => a.in - b.in)
      .slice(0, 6);
  }, [employees, days, today]);

  return (
    <SectionCard title="Upcoming Birthdays" icon={Cake}>
      {list.length === 0 ? (
        <EmptyState compact icon={Cake} title="No birthdays soon" description={`No birthdays in the next ${days} days.`} />
      ) : (
        <ul className="space-y-1">
          {list.map(({ e, d, in: n }) => (
            <li key={e.employee_id || e.id} className="flex items-center gap-3 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-muted/60">
              <Avatar emp={e} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{fullName(e)}</p>
                <p className="truncate text-xs text-muted-foreground">{e.designation || e.department || '—'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-foreground">{fmtMonthDay(d)}</p>
                <Badge variant={n === 0 ? 'success' : 'soft'} className="mt-0.5">{relativeDay(n)}</Badge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

// ---------- Work anniversaries ----------
export function WorkAnniversaries({ employees = [], days = 30 }) {
  const today = useMemo(() => new Date(), []);
  const list = useMemo(() => {
    return employees
      .map((e) => ({ e, d: parseDate(e.date_of_joining) }))
      .filter((x) => x.d && x.d < today)
      .map((x) => ({ ...x, in: daysUntilAnnual(x.d, today), years: today.getFullYear() - x.d.getFullYear() }))
      .filter((x) => x.in !== null && x.in <= days && x.years > 0)
      .sort((a, b) => a.in - b.in)
      .slice(0, 6);
  }, [employees, days, today]);

  return (
    <SectionCard title="Work Anniversaries" icon={PartyPopper}>
      {list.length === 0 ? (
        <EmptyState compact icon={PartyPopper} title="No anniversaries soon" description={`No work anniversaries in the next ${days} days.`} />
      ) : (
        <ul className="space-y-1">
          {list.map(({ e, d, in: n, years }) => (
            <li key={e.employee_id || e.id} className="flex items-center gap-3 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-muted/60">
              <Avatar emp={e} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{fullName(e)}</p>
                <p className="truncate text-xs text-muted-foreground">{fmtMonthDay(d)} · {relativeDay(n)}</p>
              </div>
              <Badge variant="purple">{years} yr{years === 1 ? '' : 's'}</Badge>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

// ---------- Upcoming holidays ----------
export function UpcomingHolidays({ holidays = [] }) {
  const today = useMemo(() => new Date(), []);
  const list = useMemo(() => {
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return holidays
      .map((h) => ({ h, d: parseDate(h.date || h.holiday_date || h.day) }))
      .filter((x) => x.d && x.d >= t)
      .sort((a, b) => a.d - b.d)
      .slice(0, 6)
      .map((x) => ({ ...x, in: Math.round((x.d - t) / 86400000) }));
  }, [holidays, today]);

  return (
    <SectionCard title="Upcoming Holidays" icon={CalendarDays}>
      {list.length === 0 ? (
        <EmptyState compact icon={CalendarDays} title="No holidays scheduled" description="No upcoming holidays on the calendar." />
      ) : (
        <ul className="space-y-1">
          {list.map(({ h, d, in: n }, i) => (
            <li key={i} className="flex items-center gap-3 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-muted/60">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 leading-none">
                <span className="text-[10px] font-semibold uppercase text-primary">{MONTHS[d.getMonth()]}</span>
                <span className="text-sm font-bold text-primary">{d.getDate()}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{h.name || h.holiday_name || h.title || 'Holiday'}</p>
                <p className="text-xs text-muted-foreground">{relativeDay(n)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

// ---------- Mini calendar ----------
export function MiniCalendar({ holidays = [] }) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const holidaySet = useMemo(() => {
    const s = new Set();
    holidays.forEach((h) => {
      const d = parseDate(h.date || h.holiday_date || h.day);
      if (d && d.getFullYear() === y && d.getMonth() === m) s.add(d.getDate());
    });
    return s;
  }, [holidays, y, m]);

  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <SectionCard title={`${['January','February','March','April','May','June','July','August','September','October','November','December'][m]} ${y}`} icon={CalendarClock}>
      <div className="grid grid-cols-7 gap-1 text-center">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="pb-1 text-[10px] font-semibold uppercase text-muted-foreground">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <div key={`e${i}`} />;
          const isToday = day === today.getDate();
          const isHoliday = holidaySet.has(day);
          return (
            <div
              key={day}
              className={cn(
                'grid aspect-square place-items-center rounded-lg text-xs transition-colors',
                isToday && 'bg-primary font-semibold text-primary-foreground shadow-sm',
                !isToday && isHoliday && 'bg-danger/10 font-medium text-danger',
                !isToday && !isHoliday && 'text-foreground hover:bg-muted'
              )}
              title={isHoliday ? 'Holiday' : undefined}
            >
              {day}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-4 border-t border-border/60 pt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-primary" /> Today</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-danger/60" /> Holiday</span>
      </div>
    </SectionCard>
  );
}

// ---------- Recent hires ----------
export function RecentHires({ employees = [], days = 45, onView }) {
  const today = useMemo(() => new Date(), []);
  const list = useMemo(() => {
    return employees
      .map((e) => ({ e, d: parseDate(e.date_of_joining) }))
      .filter((x) => x.d && (today - x.d) / 86400000 <= days && x.d <= today)
      .sort((a, b) => b.d - a.d)
      .slice(0, 6);
  }, [employees, days, today]);

  return (
    <SectionCard title="Recent Hires" icon={UserPlus2}>
      {list.length === 0 ? (
        <EmptyState compact icon={UserPlus2} title="No recent hires" description={`No one joined in the last ${days} days.`} />
      ) : (
        <ul className="space-y-1">
          {list.map(({ e, d }) => (
            <li
              key={e.employee_id || e.id}
              className={cn('flex items-center gap-3 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-muted/60', onView && 'cursor-pointer')}
              onClick={onView ? () => onView(e) : undefined}
            >
              <Avatar emp={e} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{fullName(e)}</p>
                <p className="truncate text-xs text-muted-foreground">{e.designation || e.department || '—'}</p>
              </div>
              <div className="flex items-center gap-1 text-right">
                <span className="text-xs text-muted-foreground">{fmtMonthDay(d)}</span>
                {onView && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

// ---------- Workforce distribution (donut by employment type) ----------
export function WorkforceDistribution({ employees = [] }) {
  const data = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      const key = (e.employment_type || 'Unspecified').replace(/_/g, ' ');
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [employees]);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <SectionCard title="Workforce Distribution" icon={Users2}>
      {total === 0 ? (
        <EmptyState compact icon={Users2} title="No workforce data" />
      ) : (
        <div className="flex items-center gap-4">
          <div className="relative h-40 w-40 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={68} paddingAngle={2} stroke="none">
                  {data.map((_, i) => <Cell key={i} fill={chartColor(i)} />)}
                </Pie>
                <Tooltip contentStyle={tooltipContentStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              <div className="text-center">
                <p className="text-2xl font-bold tnum leading-none text-foreground">{total}</p>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total</p>
              </div>
            </div>
          </div>
          <ul className="min-w-0 flex-1 space-y-2">
            {data.slice(0, 5).map((d, i) => (
              <li key={d.name} className="flex items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: chartColor(i) }} />
                <span className="min-w-0 flex-1 truncate capitalize text-muted-foreground">{d.name}</span>
                <span className="font-semibold tnum text-foreground">{d.value}</span>
                <span className="w-10 text-right text-xs text-muted-foreground">{Math.round((d.value / total) * 100)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}

// ---------- Department summary (horizontal bars) ----------
export function DepartmentSummary({ employees = [] }) {
  const data = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      const key = e.department || 'Unassigned';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6);
  }, [employees]);

  return (
    <SectionCard title="Headcount by Department" icon={Building2}>
      {data.length === 0 ? (
        <EmptyState compact icon={Building2} title="No department data" />
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
              <XAxis type="number" {...axisProps} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={96} {...axisProps} />
              <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionCard>
  );
}

// ---------- Activity timeline ----------
export function ActivityTimeline({ employees = [], fraudAlerts = [], onViewAll }) {
  const today = useMemo(() => new Date(), []);
  const events = useMemo(() => {
    const evs = [];
    employees.forEach((e) => {
      const d = parseDate(e.date_of_joining);
      if (d && (today - d) / 86400000 <= 30 && d <= today) {
        evs.push({ ts: d, tone: 'success', icon: UserPlus2, text: `${fullName(e)} joined as ${e.designation || 'team member'}` });
      }
    });
    (Array.isArray(fraudAlerts) ? fraudAlerts : []).slice(0, 6).forEach((a) => {
      const d = parseDate(a.created_at || a.detected_at || a.timestamp);
      evs.push({ ts: d || today, tone: 'danger', icon: Activity, text: a.description || a.alert_type || a.reason || 'Attendance exception flagged' });
    });
    return evs.sort((a, b) => (b.ts || 0) - (a.ts || 0)).slice(0, 7);
  }, [employees, fraudAlerts, today]);

  const toneColor = { success: 'bg-success', danger: 'bg-danger', primary: 'bg-primary', info: 'bg-info' };

  return (
    <SectionCard
      title="Recent Activity"
      icon={Activity}
      action={onViewAll && (
        <button onClick={onViewAll} className="text-xs font-medium text-primary hover:underline">View all</button>
      )}
    >
      {events.length === 0 ? (
        <EmptyState compact icon={Activity} title="No recent activity" description="Activity across the workspace will appear here." />
      ) : (
        <ol className="relative space-y-4 pl-1">
          <span className="absolute left-[9px] top-1 bottom-1 w-px bg-border" aria-hidden />
          {events.map((ev, i) => {
            const Icon = ev.icon;
            return (
              <li key={i} className="relative flex gap-3">
                <span className={cn('z-[1] mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full ring-4 ring-card', toneColor[ev.tone] || 'bg-primary')}>
                  <Icon className="h-2.5 w-2.5 text-white" />
                </span>
                <div className="min-w-0 flex-1 pb-1">
                  <p className="text-sm leading-snug text-foreground">{ev.text}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{ev.ts ? fmtMonthDay(ev.ts) : 'Recently'}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}

export default SectionCard;
